import { Component, computed, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { lastValueFrom } from 'rxjs';
import { ThemeStateService } from 'ats-ui-lib';
import { PoButtonModule, PoNotificationService, PoToasterOrientation } from '@po-ui/ng-components';
import { AuthService } from '../../root/auth/auth.service';
import { CountdownService } from '../../root/countdown/countdown.service';
import { NoStandaloneSharedModule } from '../../no-standalone-shared/no-standalone-shared.module';
import { changeCountdown } from '../../shared/animations/change-countdown.animation';

enum EmailVerificationState {
  Loading,
  Success,
  Error,
}

@Component({
  selector: 'ats-confirm-email-verification',
  imports: [CommonModule, PoButtonModule, TranslateModule, NoStandaloneSharedModule],
  templateUrl: './confirm-email-verification.component.html',
  styleUrl: './confirm-email-verification.component.css',
  animations: [changeCountdown],
  providers: [CountdownService],
})
export class ConfirmEmailVerificationComponent implements OnInit {
  protected currentTheme$ = this.themeStateService.currentTheme$;

  protected countdownSeconds = this.countdownService.countdownSeconds;

  protected isLoadingState = computed(() => this.pageState() === EmailVerificationState.Loading);

  protected isSuccessState = computed(() => this.pageState() === EmailVerificationState.Success);

  private pageState = signal<EmailVerificationState>(EmailVerificationState.Loading);

  private redirectUrlInBase64: string | null = null;

  private readonly redirectTimeSeconds = 5;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private poNotificationService: PoNotificationService,
    private themeStateService: ThemeStateService,
    private countdownService: CountdownService,
    private authService: AuthService,
  ) {}

  public async ngOnInit(): Promise<void> {
    await this.handleEmailVerification();
  }

  protected async goToRedirectUrlOrHome(): Promise<void> {
    const redirectUrl = this.decodeRedirectUrl();
    if (!redirectUrl) {
      await this.router.navigate(['']);
      return;
    }

    window.location.href = redirectUrl;
  }

  private decodeRedirectUrl(): string | null {
    try {
      const decoded = atob(this.redirectUrlInBase64 ?? '');
      return new URL(decoded).href;
    } catch {
      return null;
    }
  }

  private async handleEmailVerification(): Promise<void> {
    const { redirectUrl, token } = this.activatedRoute.snapshot.queryParams;
    this.redirectUrlInBase64 = redirectUrl;

    await this.verifyEmail(token);
    await this.handleRedirection();
  }

  private async verifyEmail(token: string): Promise<void> {
    if (!token) {
      this.pageState.set(EmailVerificationState.Error);
      this.raiseError('l-invalid-verification-link');
      return;
    }

    this.pageState.set(EmailVerificationState.Loading);
    try {
      await lastValueFrom(this.authService.validateVerificationEmail(token));
      this.pageState.set(EmailVerificationState.Success);
    } catch (error) {
      this.pageState.set(EmailVerificationState.Error);
      this.raiseError('l-email-verification-error');
    }
  }

  private raiseError(errorLabel: string): void {
    this.poNotificationService.error({
      orientation: PoToasterOrientation.Top,
      message: this.translateService.instant(errorLabel),
    });
  }

  private async handleRedirection(): Promise<void> {
    this.countdownService.init(this.redirectTimeSeconds);

    const waitTimeToChangePage = (this.redirectTimeSeconds - 0.1) * 1_000;
    setTimeout(async () => {
      await this.goToRedirectUrlOrHome();
    }, waitTimeToChangePage);
  }
}
